











































































import { computed, defineComponent, getCurrentInstance, ref } from "@vue/composition-api";
import { debounce } from "lodash";
import { Location } from "vue-router";
import { Group } from "@scrile/api-provider/dist/api/GroupsProvider";
import { FragmentFields, FragmentFieldsTyped } from "@scrile/api-provider/dist/api/types";
import { UserPublicData as OrigUserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import { getProfileUrl } from "@/lib/urlHelpers";
import BaseAvatar from "@/components/BaseAvatar";
import BaseButton from "@/components/BaseButton";
import BaseCheckbox from "@/components/BaseCheckbox";
import BaseIcon from "@/components/BaseIcon";
import BaseInput from "@/components/BaseInput";
import BasePage from "@/components/BasePage";
import BaseSpinner from "@/components/BaseSpinner";
import EmptyContent from "@/components/EmptyContent";
import GroupMenu from "@/components/GroupMenu";
import InfiniteScroll from "@/components/InfiniteScroll";
import ListItem from "@/components/ListItem";
import PageTitle from "@/components/PageTitle";

export type GroupWithUser = Pick<
  Group,
  "id" | "ownerId" | "name" | "memberIds" | "system" | "createTime" | "updateTime"
> & {
  members: (UserPublicData | null)[];
};

const USER_FRAGMENT: FragmentFields<UserPublicData> = [
  "id",
  "screenName",
  "username",
  { onlineStatus: ["isOnline"] },
  { avatar: ["urlPart", "uuid", "updateTime", "metadata"] },
  { subscriptionToMe: ["amount", { recurringBillingSchedule: ["cancelled"] }] },
];

function notEmpty<T>(value: T | null): value is T {
  return value !== null;
}

export default defineComponent({
  name: "PageGroupUsers",
  components: {
    InfiniteScroll,
    BaseCheckbox,
    BasePage,
    PageTitle,
    BaseButton,
    BaseAvatar,
    ListItem,
    GroupMenu,
    EmptyContent,
    BaseInput,
    BaseIcon,
    BaseSpinner,
  },
  props: {
    groupId: { type: String, required: true },
  },
  setup(props) {
    const { authUserStrict } = useAuthUser();
    const loading = ref(false);
    const enableScroll = ref(false);
    const group = ref<GroupWithUser>();
    const term = ref("");
    const filterLoading = ref(false);
    const searchUsers = ref<UserPublicData[]>([]);
    const showList = computed(() => {
      return term.value.length > 1 && searchUsers.value.length > 0;
    });
    const addingUserIds = ref<string[]>([]);
    const membersList = computed(() => {
      if (showList.value) {
        return searchUsers.value;
      }
      return group.value?.members?.filter(notEmpty) ?? [];
    });

    const isMemberCurrentlySubscribed = (member: UserPublicData) => {
      return member.subscriptionToMe?.recurringBillingSchedule?.cancelled === false;
    };

    const isPredefinedGroup = computed(() => isNaN(Number(props.groupId)));
    const getGroupData = async () => {
      group.value = (
        await providers.GroupsProvider.search({ data: { id: props.groupId, ownerId: authUserStrict.value.id } }, [
          "id",
          "ownerId",
          "name",
          "memberIds",
          "system",
          "createTime",
          "updateTime",
          {
            members: USER_FRAGMENT as FragmentFieldsTyped<OrigUserPublicData>,
          },
        ])
      ).find(i => i.id === props.groupId) as GroupWithUser;
    };

    const getData = async () => {
      try {
        loading.value = true;
        if (!isPredefinedGroup.value) {
          await getGroupData();
        }
      } finally {
        loading.value = false;
      }
    };

    getData();

    const profile = (user: UserPublicData): Location => {
      return getProfileUrl(user);
    };

    const getSearchUsers = debounce(async (stringAfterAt: string) => {
      try {
        searchUsers.value = await providers.UserPublicDataProvider.relevantSearchByName(
          {
            name: stringAfterAt,
          },
          USER_FRAGMENT
        );
      } finally {
        filterLoading.value = false;
      }
    }, 500);

    const setTerm = async (termValue: string) => {
      term.value = termValue;
      filterLoading.value = true;
      if (term.value.length > 1) {
        await getSearchUsers(term.value);
      } else {
        getSearchUsers.cancel();
        filterLoading.value = false;
      }
    };

    const setAddingUserId = (id: string) => {
      addingUserIds.value.push(id);
      setTimeout(() => {
        addingUserIds.value = addingUserIds.value.filter(i => i !== id);
      }, 1000);
    };

    const hasUser = (id: string): boolean => {
      return group.value?.members?.some(user => user?.id === id) ?? false;
    };

    const onSelectUser = async (id: string) => {
      const userShouldBeInGroup = !hasUser(id);
      const response = (
        await providers.GroupsProvider.updateMembers({
          data: [{ id: group.value?.id ?? "", memberId: id, status: userShouldBeInGroup }],
        })
      )[0];
      if (!userShouldBeInGroup && group.value) {
        group.value.members = group.value.members?.filter(i => i?.id !== id);
      } else if (userShouldBeInGroup && response.memberIds.some(i => i === id) && group.value) {
        const newUserItem = searchUsers.value.find(i => i.id === id);
        if (!newUserItem) return;
        setAddingUserId(id);

        group.value = {
          ...group.value,
          members: group.value?.members?.concat([newUserItem]),
        };
      }
    };

    const vm = getCurrentInstance();

    const onItemClick = (user: UserPublicData) => {
      const url = profile(user);
      vm?.$router.push(url);
    };

    return {
      loading,
      getData,
      group,
      membersList,
      onItemClick,
      isPredefinedGroup,
      profile,
      enableScroll,
      term,
      filterLoading,
      setTerm,
      hasUser,
      addingUserIds,
      onSelectUser,
      isMemberCurrentlySubscribed,
    };
  },
});
